import { StreemAPI, APIError, StreemApiRoomVideoPlatform, StreemApiCreateRoomRequestPreferredCodec, } from '@streem/api';
import { fetchClosestMediaRegion } from '@streem/sdk-chime-video';
import { DeviceId, RoomId } from '@streem/domain-id';
import { invariant } from '@streem/toolbox';
import { BehaviorSubject, from, of } from 'rxjs';
import { catchError, distinctUntilChanged, exhaustMap, filter, map, tap, withLatestFrom, } from 'rxjs/operators';
import { StreemError } from '../../streem_error';
import { buildRoomInviteData } from '../../util/build_room_invitation';
import { logger } from '../../util/logger';
import { filterNull } from '../../util/rx/filter_null';
import { firstFromGroup } from '../../util/rx/first_from_group';
import SdkDebug from '../../util/toggles/sdk_debug';
import { RoomTokenController } from '../controllers/room_token_controller';
import { UserWall } from '../wall/user_wall';
import { UserWallSyncBuilder } from '../wall/wall_sync_builder';
import { DetailSession } from './detail_session';
import { HostRoomSession } from './host_room_session';
import { OnSiteRoomSession } from './onsite_room_session';
import { SpectatorRoomSession } from './spectator_room_session';
export class UserSession {
    static async initialize(config) {
        const wallSyncClient = config.wallSyncClient ||
            (await new UserWallSyncBuilder(config.userId, config.deviceId).build());
        return new UserSession(config, wallSyncClient);
    }
    constructor(config, wallSyncClient) {
        this.config = config;
        this.roomSessionSubject = new BehaviorSubject(undefined);
        this.roomSession = this.roomSessionSubject.asObservable();
        this.clearRoomSession = () => {
            this.roomSessionSubject.next(undefined);
        };
        logger.info(`Creating new UserSession for userId=${config.userId}`);
        this.roomTokenController = new RoomTokenController();
        this.userWall = new UserWall({
            localUserId: this.config.userId,
            localDeviceId: this.config.deviceId,
            wallSyncClient: wallSyncClient,
        });
        this.userWallConnectedYn = this.userWall.connected;
        this.userWallReconnectFailed = this.userWall.reconnectFailed;
        this.lastIdentifiedDevice = this.userWall.userDeviceUpdates.pipe(firstFromGroup(), filterNull(), distinctUntilChanged((prev, curr) => prev?.deviceSid === curr?.deviceSid), filter(device => {
            invariant(device.deviceSid, 'Device should always have a sid');
            return !this.config.deviceId.compare(new DeviceId(device.deviceSid));
        }), tap(device => {
            logger.info(`Detected login from another device with sid ${device?.deviceSid}`);
        }));
        this.currentSmsDeliveryStatus = this.userWall.smsDeliveryStatuses.pipe(firstFromGroup(), filterNull(), distinctUntilChanged());
        this.groupReservations = this.userWall.groupReservations.pipe(distinctUntilChanged());
        this.currentAvailabilityStatus = this.userWall.userAvailabilityStatus.pipe(distinctUntilChanged());
        this.currentPendingInvitation = this.userWall.currentPendingInvite.pipe(withLatestFrom(this.roomSessionSubject), exhaustMap(([invitation, roomSession]) => {
            if (!invitation) {
                logger.info(`setting current pending invitation to undefined`);
                return of(undefined);
            }
            // Attempting to rejoin the existing room? Automatically accept
            invariant(invitation?.roomSid, 'Invitation must have a roomSid');
            const roomIdMatchesCurrentRoom = invitation &&
                roomSession &&
                roomSession.roomId.compare(new RoomId(invitation.roomSid));
            if (roomIdMatchesCurrentRoom) {
                return this.acceptRoomInvitationAndRejoinRoom(invitation);
            }
            // This is a new invite. Let's fetch info about the user
            invariant(invitation.fromUserSid, 'Pending invitation must have fromUserSid');
            return this.getUserAndBuildInvite(invitation);
        }));
    }
    async connect() {
        await this.userWall.connect();
    }
    async disconnect() {
        this.userWall.disconnect();
        this.roomTokenController.disconnect();
    }
    /**
     * Get the SMS delivery status of an invitation by invitation sid
     *
     * NOTE 2022-05-18 Taylor: I don't think this function is working entirely as intended.
     * The only time this pipe will emit is if the last changed sms delivery status happens to be the id we're looking for,
     * which can be missed if events fire quickly.
     */
    getSmsInviteStatusBySid(sid) {
        return this.currentSmsDeliveryStatus.pipe(filter(status => status?.invitationSid === sid), tap(status => {
            if (!status)
                return;
            logger.info(`SMS delivery status changed for invitation '${status.invitationSid}', message: '${status.messageId}'. New status: '${status.deliveryStatus}'`);
        }));
    }
    async acceptInvitation(invitation, callConfig) {
        logger.info('Invitation received, creating SpectatorRoomSession');
        if (SdkDebug.allowFaceToFaceMode) {
            callConfig.faceToFaceModeEnabled = true;
        }
        logger.info('Attempting to use invitation code: ', invitation.id.toString());
        try {
            await StreemAPI.rooms.acceptRoomInvitation(invitation.roomId.toString(), invitation.id.toString(), {
                acceptedByUserSid: this.config.userId.toString(),
            });
            logger.info('Invitation has been accepted');
        }
        catch (error) {
            const roomAcceptError = new StreemError('Invitation could not be accepted.');
            roomAcceptError.cause = error;
            throw roomAcceptError;
        }
        await this.roomTokenController.fetchRoomVideoCredentials(invitation.roomId, this.config.userId);
        if (!this.roomTokenController.hasToken()) {
            throw new StreemError('Unable to accept invitation. No room video credentials found.');
        }
        const sessionConfig = {
            onEnd: this.clearRoomSession,
            localUserId: this.config.userId,
            localDeviceId: this.config.deviceId,
            remoteUserId: invitation.fromUserId,
            remoteDeviceId: invitation.fromDeviceId,
            roomId: invitation.roomId,
            callSettings: this.getCallSettings(callConfig),
        };
        const spectatorRoomSession = await SpectatorRoomSession.initialize(sessionConfig);
        try {
            await spectatorRoomSession.connectToRoom();
        }
        catch (error) {
            await spectatorRoomSession.leaveRoom();
            const connectionError = new StreemError('Spectator failed to connect to room after accepting invitation');
            connectionError.cause = error;
            throw connectionError;
        }
        this.roomSessionSubject.next(spectatorRoomSession);
        return spectatorRoomSession;
    }
    async rejectInvitation(invitation) {
        try {
            await StreemAPI.rooms.rejectRoomInvitation(invitation.roomId.toString(), invitation.id.toString(), {
                rejectedByUserSid: this.config.userId.toString(),
            });
            logger.info(`${this.config.userId} has rejected invitation ${invitation.id} for room ${invitation.roomId}`);
        }
        catch (error) {
            logger.error(`Failed to reject invitation ${invitation.id}: `, error);
            throw error;
        }
    }
    async startOnSiteStreem(onsiteConfig) {
        try {
            const room = await this.createOrGetRoom(onsiteConfig);
            if (!room || !room.sid) {
                throw new StreemError('Failed to create room for remote streem.');
            }
            const roomId = new RoomId(room.sid);
            await this.roomTokenController.fetchRoomVideoCredentials(roomId, this.config.userId);
            if (!this.roomTokenController.hasToken()) {
                throw new StreemError('Failed to start on site streem. No room video credentials found');
            }
            logger.debug('Starting OnSiteStreem');
            const roomSession = await OnSiteRoomSession.initialize({
                onEnd: this.clearRoomSession,
                localUserId: this.config.userId,
                localDeviceId: this.config.deviceId,
                remoteUserId: this.config.userId,
                remoteDeviceId: this.config.deviceId,
                roomId: roomId,
                callSettings: this.getCallSettings({
                    ...onsiteConfig.callConfig,
                    faceToFaceModeEnabled: false,
                }),
            });
            this.roomSessionSubject.next(roomSession);
            await roomSession.startOnSiteCall();
            return roomSession;
        }
        catch (error) {
            logger.error('startOnsiteStreem failed', error);
            this.clearRoomSession();
            throw new StreemError(`Internal Error: ${error}`);
        }
    }
    async startRemoteStreem(remoteStreemConfig) {
        logger.info('startRemoteStreem has been called');
        try {
            // This is only for twilio. If we don't send a preferred codec, the backend will default to h264
            // when creating the twilio room. We can't prevent sending a preferred codec for chime since
            // we first have to call createOrGetRoom, which will then let us know if we are using twilio or chime.
            const preferredVideoCodec = remoteStreemConfig.preferredCodec || StreemApiCreateRoomRequestPreferredCodec.VP9;
            let mediaRegion = undefined;
            logger.info(`Attempting to createOrGetRoom with userId=${this.config.userId} deviceId=${this.config.deviceId}`);
            const room = await this.createOrGetRoom({
                ...remoteStreemConfig,
                preferredCodec: preferredVideoCodec,
            });
            invariant(room, 'Room is required to create a room session');
            const roomId = new RoomId(room.sid);
            if (room.videoPlatform === StreemApiRoomVideoPlatform.CHIME) {
                mediaRegion = await fetchClosestMediaRegion();
                logger.info(`Closest region is ${mediaRegion}`);
            }
            await this.roomTokenController.fetchRoomVideoCredentials(roomId, this.config.userId, mediaRegion);
            if (!this.roomTokenController.hasToken()) {
                throw new StreemError('Failed to start remote streem. No room video credentials found');
            }
            // Create non expert session
            const sessionConfig = {
                onEnd: this.clearRoomSession,
                localUserId: this.config.userId,
                localDeviceId: this.config.deviceId,
                remoteDeviceId: remoteStreemConfig.withDeviceId,
                remoteUserId: remoteStreemConfig.withUserId,
                roomId: roomId,
                callSettings: this.getCallSettings(remoteStreemConfig.callConfig),
                isHostSession: true,
            };
            logger.info(`Starting a remote streem for roomId=${roomId} localUserId=${this.config.userId} deviceId=${this.config.deviceId}`);
            const hostRoomSession = await HostRoomSession.initialize(sessionConfig);
            this.roomSessionSubject.next(hostRoomSession);
            return hostRoomSession;
        }
        catch (error) {
            logger.error('startRemoteStreem failed', error);
            this.clearRoomSession();
            if (error instanceof APIError) {
                throw new APIError('API Error', error.response);
            }
            else {
                throw new StreemError(`Internal Error: ${error}`);
            }
        }
    }
    async startDetailSession(roomSid, transcriptionsEnabledYn) {
        const detailSession = await DetailSession.initialize({
            userId: this.config.userId,
            deviceId: this.config.deviceId,
            roomId: new RoomId(roomSid),
            readOnly: false,
            transcriptionsEnabledYn,
        });
        await detailSession.connect();
        return detailSession;
    }
    async createOrGetRoom(config) {
        if (config.roomId) {
            try {
                const { room } = await StreemAPI.rooms.getRoom(config.roomId);
                return room;
            }
            catch (error) {
                throw new StreemError(`Failed to getRoom for roomId=${config.roomId} userId=${this.config.userId} deviceId=${this.config.deviceId} error=${error}`);
            }
        }
        else {
            try {
                const { room } = await StreemAPI.rooms.createRoom({
                    createdByUserSid: this.config.userId.toString(),
                    referenceId: config.referenceId,
                    integrationId: config.integrationId,
                    preferredCodec: config.preferredCodec,
                });
                return room;
            }
            catch (error) {
                throw new StreemError(`Failed to createRoom for userId=${this.config.userId} deviceId=${this.config.deviceId} error=${error}`);
            }
        }
    }
    getUserAndBuildInvite(invitation) {
        invariant(invitation.fromUserSid, 'Pending invitation must have fromUserSid');
        return from(StreemAPI.users.getUser(invitation.fromUserSid)).pipe(map(userResponse => {
            invariant(userResponse.user, 'User is required to build an room invite data');
            logger.info(`setting current pending invitation to ${invitation}`);
            return buildRoomInviteData(invitation, userResponse.user);
        }), catchError(error => {
            logger.error(`Attempted to find user ${invitation.fromUserSid} from invitation ${invitation.sid} and failed.`, error);
            return of(undefined);
        }));
    }
    acceptRoomInvitationAndRejoinRoom(invitation) {
        invariant(invitation?.roomSid, 'Invitation must have a roomSid');
        logger.info('Attempting to re-join existing room, accepting new invite.', invitation.id);
        StreemAPI.rooms.acceptRoomInvitation(invitation.roomSid, invitation.id, {
            acceptedByUserSid: this.config.userId.toString(),
        });
        return of(undefined);
    }
    getCallSettings(callConfig) {
        const credentialsAndPlatform = this.roomTokenController.roomVideoCredentialsAndPlatform;
        return {
            ...callConfig,
            ...credentialsAndPlatform,
        };
    }
}
//# sourceMappingURL=user_session.js.map